import { useMemo } from 'react'

import _ from 'lodash'

import { useJourneysListView } from '@/features/journeys/hooks/useJourneysListView'
import { MISSION_SORT_PRIORITY } from '@/features/missions/constants/status'
import { useMissionsListView } from '@/features/missions/hooks/useMissionsListView'
import { isMissionActionable } from '@/features/missions/lib/missionUiStatus'

export type FeaturedQuest = {
  title: string
  description: string | null
  to: string
  status: 'progress' | 'available'
  percent: number
  pointReward: number
  coinReward: number
  imageUrl: string | null
  ctaLabel: string
}

export type NextMissionItem = {
  key: string
  title: string
  to: string
  status: 'progress' | 'available'
  percent: number
  pointReward: number
  coinReward: number
  isEndingSoon: boolean
}

/* Recusada entra na home como não iniciada (0%): a evidência volta a ser enviada
   do zero, e o rótulo diz isso em vez de "Iniciar". */
const missionCtaLabel = (uiStatus: string): string => {
  if (uiStatus === 'progress') return 'Continuar'
  if (uiStatus === 'danger') return 'Reenviar'
  return 'Iniciar'
}

export const useHomeView = () => {
  const journeys = useJourneysListView()
  const missions = useMissionsListView()

  const pendingJourneys = useMemo(
    () =>
      journeys.allItems.filter(
        journey =>
          (journey.uiStatus === 'progress' || journey.uiStatus === 'available') &&
          journey.blockResult.unlocked
      ),
    [journeys.allItems]
  )

  const pendingMissions = useMemo(() => {
    const active = missions.allItems.filter(mission =>
      isMissionActionable(mission.uiStatus)
    )

    return [...active].sort((a, b) => {
      if (a.uiStatus !== b.uiStatus) {
        return MISSION_SORT_PRIORITY[a.uiStatus] - MISSION_SORT_PRIORITY[b.uiStatus]
      }
      if (a.isEndingSoon !== b.isEndingSoon) return a.isEndingSoon ? -1 : 1
      return (a.position ?? 0) - (b.position ?? 0)
    })
  }, [missions.allItems])

  // Destaque do Quest Book: jornada acionável (em andamento primeiro);
  // sem jornadas pendentes, a melhor missão assume o posto.
  const { featured, featuredMissionId } = useMemo((): {
    featured: FeaturedQuest | null
    featuredMissionId: number | null
  } => {
    const nextJourney =
      pendingJourneys.find(journey => journey.uiStatus === 'progress') ??
      pendingJourneys[0]

    if (nextJourney) {
      const status = nextJourney.uiStatus === 'progress' ? 'progress' : 'available'
      return {
        featuredMissionId: null,
        featured: {
          title: nextJourney.name,
          description: nextJourney.description ?? null,
          to: `/jornadas/${nextJourney.uid}`,
          status,
          percent: Math.round(
            (nextJourney._progress?.finishedPercent ?? 0) * 100
          ),
          pointReward: nextJourney._totalPointReward ?? 0,
          coinReward: nextJourney._totalCoinReward ?? 0,
          imageUrl: _.get(nextJourney, 'cover.file.url', null),
          ctaLabel: status === 'progress' ? 'Continuar' : 'Iniciar'
        }
      }
    }

    const nextMission = pendingMissions[0]
    if (nextMission) {
      const status = nextMission.uiStatus === 'progress' ? 'progress' : 'available'
      return {
        featuredMissionId: nextMission.id,
        featured: {
          title: nextMission.name,
          description: nextMission.description ?? null,
          to: `/missoes/${nextMission.uid}`,
          status,
          percent: status === 'progress' ? 50 : 0,
          pointReward: nextMission.point_reward ?? 0,
          coinReward: nextMission.coin_reward ?? 0,
          imageUrl: _.get(nextMission, 'image.file.url', null),
          ctaLabel: missionCtaLabel(nextMission.uiStatus)
        }
      }
    }

    return { featured: null, featuredMissionId: null }
  }, [pendingJourneys, pendingMissions])

  // Sem corte: o bloco `missions` da home aplica o `settings.limit` que o
  // produtor configurou. `nextMissions` continua com os três de sempre porque é
  // o que o `QuestBookCard` desenha embaixo do destaque.
  const allNextMissions = useMemo(
    (): NextMissionItem[] =>
      pendingMissions
        .filter(mission => mission.id !== featuredMissionId)
        .map(mission => {
          const status =
            mission.uiStatus === 'progress' ? 'progress' : 'available'
          return {
            key: `mission-${mission.id}`,
            title: mission.name,
            to: `/missoes/${mission.uid}`,
            status,
            // Missões são binárias — submissão em análise lê como metade do caminho.
            percent: status === 'progress' ? 50 : 0,
            pointReward: mission.point_reward ?? 0,
            coinReward: mission.coin_reward ?? 0,
            isEndingSoon: mission.isEndingSoon
          }
        }),
    [pendingMissions, featuredMissionId]
  )

  const nextMissions = useMemo(
    () => allNextMissions.slice(0, 3),
    [allNextMissions]
  )

  return {
    featured,
    nextMissions,
    allNextMissions,
    journeysProgress: journeys.globalProgress,
    missionsTotal: missions.allItems.length,
    missionsCompleted: missions.globalProgress.completed,
    /**
     * Quantas missões o participante ainda pode fazer — **incluindo** a que foi
     * promovida a destaque.
     *
     * `allNextMissions` não serve para saber isso: ele exclui `featuredMissionId`.
     * Num evento com uma única missão acionável e nenhuma jornada pendente,
     * aquela lista fica vazia com a missão visível logo acima, e quem se guiar
     * por ela anuncia ausência de missão na cara da missão.
     */
    pendingMissionsCount: pendingMissions.length,
    hasJourneys: !journeys.isEmpty,
    isLoading: journeys.isLoading || missions.isLoading
  }
}
